import axios from "axios";
import { toast } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Attach JWT token automatically
const authAxios = axios.create({
  baseURL: BACKEND_URL,
});

// Device-trust tokens (OTP 2FA "remember this device for 7 days") are
// scoped per login email, not one shared browser-wide key. A single
// global "deviceTrustToken" key meant that logging in as a second email
// on the same browser could end up sending the FIRST account's token
// along with the second account's credentials - harmless server-side
// (login() filters by both user_id and the token's hash, so a mismatched
// token is simply ignored and OTP is still required - see
// backend/app/routes/auth/auth.py) but confusing/fragile, and reported as
// "it skipped OTP for a second account" - keying storage by email removes
// the ambiguity entirely rather than relying on the backend check alone.
const deviceTrustKey = (email) => `deviceTrustToken:${(email || "").trim().toLowerCase()}`;

authAxios.interceptors.request.use((config) => {
  const authKey = localStorage.getItem("auth");
  if (authKey) {
    const localData = JSON.parse(authKey);
    config.headers.Authorization = `Bearer ${localData.token}`;
  }
  // Always send JSON
  if (!config.headers["Content-Type"]) {
    config.headers["Content-Type"] = "application/json";
  }

  return config;
});

authAxios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { response } = error;
    let errorMessage = 'An unexpected error occurred. Please try again.';

    if (response) {
      const { status, data } = response;

      // Was there actually a token on this request? authAxios's own
      // request interceptor above only sets Authorization "if (authKey)" -
      // so no stored session means no header went out at all. This is
      // what distinguishes the two different causes of a 403 from this
      // backend: the global JWT gate (app/__init__.py) returns 403 (not
      // 401) specifically when no/invalid identity is present - exactly
      // what happens browsing back to a protected page after already
      // being logged out. But plenty of *legitimate*, logged-in 403s
      // also exist throughout this app (branch/role permission checks,
      // e.g. a receptionist hitting a doctor-only route) - those must
      // never clear a valid session or redirect, just show the toast as
      // before.
      const hadToken = (() => {
        try {
          return !!JSON.parse(localStorage.getItem("auth") || "{}").token;
        } catch {
          return false;
        }
      })();

      switch (status) {
        case 401:
          errorMessage = 'Unauthorized: Invalid or expired token. Please login.';
          // Clear auth data and redirect to login
          localStorage.removeItem("auth");
          localStorage.removeItem("token");
          // Only redirect if not already on login page and not a login request
          const isLoginRequest = error.config?.url?.includes("/login");
          const isLoginPage = window.location.pathname === "/" || window.location.pathname === "/login";
          if (!isLoginRequest && !isLoginPage) {
            // Use setTimeout to allow toast to show first
            setTimeout(() => {
              window.location.href = "/";
            }, 1000);
          }
          break;
        case 403:
          if (!hadToken) {
            // Not actually a permission problem - there was no session
            // to begin with (e.g. browser-back to a protected page after
            // logging out). Same recovery as 401.
            errorMessage = 'You need to log in to view this page.';
            localStorage.removeItem("auth");
            localStorage.removeItem("token");
            const isLoginPage403 = window.location.pathname === "/" || window.location.pathname === "/login";
            if (!isLoginPage403) {
              setTimeout(() => {
                window.location.href = "/";
              }, 1000);
            }
          } else {
            // A real, logged-in permission denial - leave the session
            // alone, just surface the toast.
            errorMessage = 'Forbidden: You do not have permission.';
          }
          break;
        case 402:
          if (data?.error === "subscription_expired") {
            // Org's subscription is past its grace period - almost every
            // other API call will 402 the same way, so send the user to
            // the dedicated lock screen instead of a toast they'd see on
            // every page. Login itself is unaffected by this gate.
            errorMessage = data?.message || 'Your organization\'s subscription has expired.';
            if (window.location.pathname !== "/subscription-locked") {
              window.location.href = "/subscription-locked";
            }
            return Promise.reject(error);
          }
          errorMessage = 'Insufficient credit: Your balance is low. Please recharge.';
          break;
        case 404:
          errorMessage = 'Not Found: The requested resource was not found.';
          break;
        case 500:
          errorMessage = 'Server Error: An unexpected error occurred. Please try after sometime.';
          break;
        default:
          errorMessage = data?.error || data?.message || `Error with status code: ${status}`;
      }
    } else if (error.request) {
      errorMessage = 'Network Error: No response received from the server. Please check internet connection.';
    }

    // Display the error toast notification with custom styling
    toast.error(errorMessage, {
      position: "top-right",
      theme: "light",
      style: {
        backgroundColor: 'white',
        color: 'red',
        fontWeight: 'bold',
      },
    });

    return Promise.reject(error);
  }
);

const api = {
  getConsultationReports: (id) => authAxios.get(`/consultations/${id}/reports`),
  uploadConsultationReport: (id, formData) => authAxios.post(`/consultations/${id}/reports`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  getReportDownloadUrl: (reportId) => authAxios.get(`/reports/${reportId}/download`),
  deleteReport: (reportId) => authAxios.delete(`/reports/${reportId}`),
  fetchDoctorSlots: (doctorId, date) =>
    authAxios.get(`/patients/doctor-slots/${doctorId}?date=${date}`),
  registerUser: (formData) =>
    authAxios.post("/users/", { data: formData }),
  loginUser: (username, password) =>
    // device_token: set once THIS email has completed OTP 2FA on this
    // browser (see verifyLoginOtp/storeDeviceTrustToken below) - sending
    // it back lets the backend skip OTP for DEVICE_TRUST_DAYS (7) without
    // the user needing it. Looked up by the email being submitted, not a
    // single shared key, so switching emails on the same browser can
    // never attach a different account's token. Absent/null on a
    // first-ever login for this email from this browser.
    axios.post(`${BACKEND_URL}/login`, { username, password, device_token: localStorage.getItem(deviceTrustKey(username)) }, {
      headers: {
        'Content-Type': 'application/json'
      }
    }),
  // Called on successful OTP verification to persist the device_token
  // under this specific email's key (see deviceTrustKey above).
  storeDeviceTrustToken: (email, token) => {
    localStorage.setItem(deviceTrustKey(email), token);
  },
  // Pre-auth calls (OTP 2FA) - deliberately plain axios, not authAxios,
  // same reasoning as loginUser above: no full session token exists yet,
  // only the short-lived pre_auth_token from /login's otp_required
  // response, sent manually per-call rather than via the authAxios
  // interceptor (which only ever reads the "auth" localStorage key).
  verifyLoginOtp: (preAuthToken, otp) =>
    axios.post(`${BACKEND_URL}/login/verify-otp`, { otp }, {
      headers: { Authorization: `Bearer ${preAuthToken}` },
    }),
  resendLoginOtp: (preAuthToken, channel) =>
    axios.post(`${BACKEND_URL}/login/resend-otp`, channel ? { channel } : {}, {
      headers: { Authorization: `Bearer ${preAuthToken}` },
    }),
  // Forgot/reset password - also plain axios, no session/pre-auth token
  // exists at any point in this flow (auth is the emailed token itself).
  forgotPassword: (email) =>
    axios.post(`${BACKEND_URL}/login/forgot-password`, { email }),
  resetPassword: (token, newPassword) =>
    axios.post(`${BACKEND_URL}/login/reset-password`, { token, new_password: newPassword }),
  selectOrg: (userId, role, orgId) =>
    axios.post(`${BACKEND_URL}/select_org`, {
      user_id: userId,
      role: role,
      org_id: orgId,
    }),

  //profile
  getAllVirtualPatientProfiles: () => authAxios.get("/profiles/"),
  getProfileDetails: () => authAxios.get("/profiles/details/"),
  getTraumaTypes: () => authAxios.get("/profiles/trauma-types/"),
  changePassword: (data) => authAxios.post("/profiles/change-password/", data),

  //Audio and visemes
  getAudioTTS: (data) => authAxios.post("/tts/audio/", { text: data }),
  getVisemes: (data) => authAxios.post("tts/visemes/", { text: data }),

  // 🔹 Consultations API
  getAllConsultations: (params = {}) => authAxios.get("/consultations/", { params }),
  getConsultation: (id) => authAxios.get(`/consultations/${id}`),
  getRawSignals: (id) => authAxios.get(`/consultations/${id}/raw-signals`),
  getConsultationConversation: (id, params = {}) => authAxios.get(`/consultations/${id}/conversation`, { params }),
  getAllConsultationsOfPatient: (id) => authAxios.get(`/consultations/patient/${id}`),
  createConsultation: (data) => authAxios.post("/consultations/", data),
  updateConsultation: (id, data) => authAxios.put(`/consultations/${id}`, data),
  deleteConsultation: (id) => authAxios.delete(`/consultations/${id}`),
  generateConsultationSummary: (id, data) => authAxios.post(`/consultations/${id}/generate-summary`, data),
  // Dashboard statistics
  getSessionCount: (doctorId = null, startDate = null, endDate = null, branchId = null) => {
    const params = {};
    if (doctorId) params.doctor_id = doctorId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (branchId) params.branch_id = branchId;
    return authAxios.get("/consultations/dashboard/session-count", { params });
  },
  getDueFollowUps: (doctorId = null, branchId = null, lookaheadDays = null) => {
    const params = {};
    if (doctorId) params.doctor_id = doctorId;
    if (branchId) params.branch_id = branchId;
    if (lookaheadDays != null) params.lookahead_days = lookaheadDays;
    return authAxios.get("/consultations/dashboard/due-follow-ups", { params });
  },
  updateFollowUpItem: (id, data) => authAxios.patch(`/consultations/follow-ups/${id}`, data),
  getSelfRatingData: (doctorId = null) => {
    const params = doctorId ? { params: { doctor_id: doctorId } } : {};
    return authAxios.get("/consultations/dashboard/self-rating-data", params);
  },
  getCognitiveAccuracyStats: (doctorId = null) => {
    const params = doctorId ? { params: { doctor_id: doctorId } } : {};
    return authAxios.get("/consultations/dashboard/cognitive-accuracy-stats", params);
  },
  getCaseComplexityStats: (doctorId = null, startDate = null, endDate = null, branchId = null) => {
    const params = {};
    if (doctorId) params.doctor_id = doctorId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (branchId) params.branch_id = branchId;
    return authAxios.get("/consultations/dashboard/complexity-stats", { params });
  },
  getDoctorsList: () => authAxios.get("/consultations/dashboard/doctors-list"),
  getCompetencyHeatmap: (doctorId = null) => {
    const params = doctorId ? { params: { doctor_id: doctorId } } : {};
    return authAxios.get("/consultations/dashboard/competency-heatmap", params);
  },
  getLongitudinalProgress: (doctorId = null) => {
    const params = doctorId ? { params: { doctor_id: doctorId } } : {};
    return authAxios.get("/consultations/dashboard/longitudinal-progress", params);
  },
  getCompetencyTrajectory: (doctorId = null, therapyType = null, startDate = null, endDate = null, branchId = null) => {
    const params = {};
    if (therapyType) params.therapy_type = therapyType;
    if (doctorId) params.doctor_id = doctorId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (branchId) params.branch_id = branchId;
    return authAxios.get("/analytics/competency-trajectory", { params });
  },
  getTherapistDashboardData: (doctorId = null, startDate = null, endDate = null) => {
    const params = {};
    if (doctorId) params.doctor_id = doctorId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return authAxios.get("/analytics/therapist/dashboard", { params });
  },
  getProgressReport: (period = 30, standard = null) =>
    authAxios.get("/analytics/org/progress-report", { params: { period, ...(standard && { standard }) } }),
  getConsultationBenchmark: (sessionId, params = {}) => authAxios.get(`/consultations/benchmark/${sessionId}`, { params }),
  getFinanceSummary: (params = {}) => authAxios.get("/analytics/finance/summary", { params }),
  getPayoutDetails: (params = {}) => authAxios.get("/analytics/finance/payout-details", { params }),

  // Supervisor
  getSupervisorTeamOverview: () => authAxios.get("/supervisor/team-overview"),
  getSupervisorReportees: () => authAxios.get("/supervisor/reportees"),
  getReporteeSessions: (reporteeId) => authAxios.get(`/supervisor/reportees/${reporteeId}/sessions`),
  assignReportee: (supervisorId, reporteeId) => authAxios.post("/supervisor/reportees", { supervisor_id: supervisorId, reportee_id: reporteeId }),
  unassignReportee: (supervisorId, reporteeId) => authAxios.delete(`/supervisor/reportees/${reporteeId}`, { params: { supervisor_id: supervisorId } }),
  getAllSupervisorMappings: () => authAxios.get("/supervisor/reportees/all"),

  // Cognitive Model Reconstruction
  getCognitiveReconstructionQuestions: (consultationId) => {
    const url = consultationId
      ? `/consultations/cognitive-reconstruction/questions?consultation_id=${consultationId}`
      : `/consultations/cognitive-reconstruction/questions`;
    return authAxios.get(url);
  },
  saveCognitiveReconstruction: (consultationId, data) => authAxios.post(`/consultations/${consultationId}/cognitive-reconstruction`, data),
  getCognitiveReconstruction: (consultationId) => authAxios.get(`/consultations/${consultationId}/cognitive-reconstruction`),
  getActualCognitiveModel: (consultationId) => authAxios.get(`/consultations/${consultationId}/cognitive-model/actual`),

  // 🔹 Patients API
  fetchPatients: (params) => authAxios.get("/patients/", { params }),
  getPatient: (id) => authAxios.get(`/patients/${id}`),
  searchPatient: (query) => authAxios.get(`/patients/search/`, {
    params: { q: query }
  }),
  createPatient: (data) => authAxios.post("/patients/", data),
  updatePatient: (id, data) => authAxios.put(`/patients/${id}`, data),

  // 🔹 Subscription API (org self-service)
  fetchSubscription: () => authAxios.get("/organizations/subscription"),
  fetchSubscriptionPayments: () => authAxios.get("/organizations/subscription/payments"),
  fetchSubscriptionInvoiceUrl: (paymentId) => authAxios.get(`/organizations/subscription/payments/${paymentId}/invoice`),
  fetchAddonQuote: () => authAxios.get("/organizations/subscription/addon/quote"),
  createSubscriptionOrder: (data) => authAxios.post("/organizations/subscription/pay", data || {}),
  fetchSubscriptionCycleOptions: () => authAxios.get("/organizations/subscription/cycle-options"),
  activateAddon: () => authAxios.post("/organizations/subscription/addon/activate"),
  deactivateAddon: () => authAxios.post("/organizations/subscription/addon/deactivate"),
  verifySubscriptionPayment: (data) => authAxios.post("/organizations/subscription/verify", data),
  // Per-session AI billing (org's own usage/dues, and paying the dues)
  fetchAiBilling: () => authAxios.get("/organizations/subscription/ai-billing"),
  payAiDues: () => authAxios.post("/organizations/subscription/ai-dues/pay"),

  // 🔹 Subscription API (platform admin, cross-tenant)
  fetchSubscriptionPlans: () => authAxios.get("/admin/subscription-plans"),
  fetchAllOrgSubscriptions: (params) => authAxios.get("/admin/subscriptions", { params }),
  fetchOrgSubscription: (orgId) => authAxios.get(`/admin/organizations/${orgId}/subscription`),
  updateOrgSubscription: (orgId, data) => authAxios.put(`/admin/organizations/${orgId}/subscription`, data),
  fetchOrgAiBilling: (orgId) => authAxios.get(`/admin/organizations/${orgId}/ai-billing`),
  fetchTrialCampaign: () => authAxios.get("/admin/trial-campaign"),
  fetchVideoCallSettings: () => authAxios.get("/admin/video-settings"),
  updateVideoCallSettings: (data) => authAxios.put("/admin/video-settings", data),
  fetchOwnBankDetails: () => authAxios.get("/organizations/bank-details"),
  fetchOnboardingStatus: () => authAxios.get("/onboarding/status"),
  fetchHelpArticles: () => authAxios.get("/help/articles"),
  askHelp: (question) => authAxios.post("/help/ask", { question }),
  // Requests from Help (backend help/feature_requests.py)
  sendHelpRequest: (data) => authAxios.post("/help/requests", data),
  fetchMyHelpRequests: () => authAxios.get("/help/requests/mine"),
  fetchTeamHelpRequests: (status) => authAxios.get("/help/requests", { params: status ? { status } : {} }),
  reviewHelpRequest: (id, data) => authAxios.put(`/help/requests/${id}`, data),
  // NL Labs inbox (platform admin)
  fetchForwardedRequests: (status) => authAxios.get("/admin/feature-requests", { params: status ? { status } : {} }),
  updateForwardedRequest: (id, data) => authAxios.put(`/admin/feature-requests/${id}`, data),
  fetchContactEnquiries: (params) => authAxios.get("/admin/contact-enquiries", { params }),
  fetchPlatformFinance: (from, to) => authAxios.get("/admin/platform-finance", { params: { from, to } }),
  fetchSmsTemplates: () => authAxios.get("/admin/sms-templates"),
  saveSmsTemplate: (flowKey, data) => authAxios.put(`/admin/sms-templates/${flowKey}`, data),
  saveSmsLimits: (data) => authAxios.put("/admin/sms-limits", data),
  testSmsTemplate: (flowKey, phone) => authAxios.post(`/admin/sms-templates/${flowKey}/test`, { phone }),
  fetchWhatsAppTemplates: () => authAxios.get("/admin/whatsapp-templates"),
  saveWhatsAppTemplate: (flowKey, data) => authAxios.put(`/admin/whatsapp-templates/${flowKey}`, data),
  testWhatsAppTemplate: (flowKey, phone) => authAxios.post(`/admin/whatsapp-templates/${flowKey}/test`, { phone }),
  saveMessagingSwitch: (channel, enabled) => authAxios.put("/admin/messaging-switches", { channel, enabled }),
  fetchMessagingSwitches: () => authAxios.get("/admin/messaging-switches"),
  saveMessagingPreferred: (preferred) => authAxios.put("/admin/messaging-switches", { preferred }),
  getPulse: () => authAxios.get("/pulse"),
  fetchNotifications: (limit = 30) => authAxios.get("/notifications", { params: { limit } }),
  markNotificationRead: (id) => authAxios.put(`/notifications/${id}/read`),
  markAllNotificationsRead: () => authAxios.put("/notifications/read-all"),
  fetchFeatures: () => authAxios.get("/features"),
  getPatientRecord: (id) => authAxios.get(`/patients/${id}/record`),
  getPatientOverview: (id) => authAxios.get(`/patients/${id}/overview`),
  getPatientCustomFields: (id) => authAxios.get(`/patients/${id}/custom-fields`),
  addPatientCustomField: (id, data) => authAxios.post(`/patients/${id}/custom-fields`, data),
  updatePatientCustomField: (id, fieldId, data) => authAxios.put(`/patients/${id}/custom-fields/${fieldId}`, data),
  getPatientProfileHistory: (id) => authAxios.get(`/patients/${id}/profile-history`),
  riskFlags: (text) => authAxios.post("/risk/flags", { text }),
  getPatientRisk: (patientId) => authAxios.get(`/patients/${patientId}/risk`),
  recordPatientRisk: (patientId, data) => authAxios.post(`/patients/${patientId}/risk`, data),
  getConsultationRisk: (consultationId) => authAxios.get(`/consultations/${consultationId}/risk`),
  getCrisisProtocol: () => authAxios.get("/crisis/protocol"),
  getCrisisRecipients: (patientId) => authAxios.get("/crisis/recipients", { params: { patient_id: patientId } }),
  raiseCrisisAlert: (data) => authAxios.post("/crisis-alerts", data),
  listCrisisAlerts: (params) => authAxios.get("/crisis-alerts", { params }),
  acknowledgeCrisisAlert: (id) => authAxios.put(`/crisis-alerts/${id}/acknowledge`),
  resolveCrisisAlert: (id, note) => authAxios.put(`/crisis-alerts/${id}/resolve`, { note }),
  messageCrisisPatient: (id, message) => authAxios.post(`/crisis-alerts/${id}/message`, { message }),
  getSafetyPlan: (patientId) => authAxios.get(`/patients/${patientId}/safety-plan`),
  saveSafetyPlan: (patientId, data) => authAxios.post(`/patients/${patientId}/safety-plan`, data),
  getSafetyPlanHistory: (patientId) => authAxios.get(`/patients/${patientId}/safety-plan/history`),
  getRiskRegister: () => authAxios.get("/risk-register"),
  getToday: () => authAxios.get("/workspace/today"),
  getMeasureDefinitions: () => authAxios.get("/measures/definitions"),
  getPatientMeasures: (patientId) => authAxios.get(`/patients/${patientId}/measures`),
  assignMeasure: (patientId, data) => authAxios.post(`/patients/${patientId}/measures/assign`, data),
  enterMeasureResponse: (patientId, data) => authAxios.post(`/patients/${patientId}/measures/responses`, data),
  cancelMeasureAssignment: (id) => authAxios.put(`/measure-assignments/${id}/cancel`),
  getNoteTemplates: () => authAxios.get("/note-templates"),
  getTreatmentPlan: (patientId) => authAxios.get(`/patients/${patientId}/treatment-plan`),
  saveTreatmentPlan: (patientId, data) => authAxios.post(`/patients/${patientId}/treatment-plan`, data),
  getFormulation: (patientId) => authAxios.get(`/patients/${patientId}/formulation`),
  getFormulationHistory: (patientId) => authAxios.get(`/patients/${patientId}/formulation/history`),
  saveFormulation: (patientId, data) => authAxios.post(`/patients/${patientId}/formulation`, data),
  draftFormulation: (patientId, model) => authAxios.post(`/patients/${patientId}/formulation/draft`, { model }),
  getPatientCheckins: (patientId) => authAxios.get(`/patients/${patientId}/checkins`),
  setCheckinFrequency: (patientId, frequency) => authAxios.put(`/patients/${patientId}/checkins/settings`, { frequency }),
  getMyTeam: () => authAxios.get("/my-team"),
  getSupervision: (box) => authAxios.get("/supervision", { params: { box } }),
  getMySupervisors: () => authAxios.get("/supervision/supervisors"),
  requestSupervision: (data) => authAxios.post("/supervision", data),
  respondSupervision: (id, data) => authAxios.post(`/supervision/${id}/respond`, data),
  scheduleSupervision: (id, data) => authAxios.post(`/supervision/${id}/schedule`, data),
  joinSupervision: (id) => authAxios.post(`/supervision/${id}/join`),
  getUpcomingSupervision: () => authAxios.get("/supervision/upcoming"),
  supervisionPreReview: (id) => authAxios.post(`/supervision/${id}/pre-review`),
  supervisionFeedback: (id, data) => authAxios.post(`/supervision/${id}/feedback`, data),
  acknowledgeSupervision: (id, data) => authAxios.post(`/supervision/${id}/acknowledge`, data),
  getOversight: (params) => authAxios.get("/oversight/metrics", { params }),
  getAuditLog: (params) => authAxios.get("/audit-log", { params }),
  exportAccessCsv: (patientId) => authAxios.get(`/audit-log/patients/${patientId}/access.csv`, { responseType: "blob" }),
  getToolkit: (params) => authAxios.get("/toolkit", { params }),
  getToolkitItem: (code) => authAxios.get(`/toolkit/${code}`),
  suggestTools: (text) => authAxios.post("/toolkit/suggest", { text }),
  getPatientHomework: (patientId, params) => authAxios.get(`/patients/${patientId}/homework`, { params }),
  getHomeworkToReview: (patientId, params) => authAxios.get(`/patients/${patientId}/homework/to-review`, { params }),
  getPreviousSession: (patientId, params) => authAxios.get(`/patients/${patientId}/previous-session`, { params }),
  assignHomework: (patientId, data) => authAxios.post(`/patients/${patientId}/homework`, data),
  cancelHomework: (patientId, id) => authAxios.put(`/patients/${patientId}/homework/${id}/cancel`),
  getVisitSummary: (patientId, params) => authAxios.get(`/patients/${patientId}/visit-summary`, { params }),
  getSessionTreatmentPlan: (patientId, params) => authAxios.get(`/patients/${patientId}/treatment-plan/session`, { params }),
  getNoteSigning: (cid) => authAxios.get(`/consultations/${cid}/signing`),
  getPatientAlerts: () => authAxios.get("/organizations/patient-alerts"),
  getOrgAlertChannels: (orgId) => authAxios.get(`/admin/organizations/${orgId}/alert-channels`),
  updateOrgAlertChannels: (orgId, data) => authAxios.put(`/admin/organizations/${orgId}/alert-channels`, data),
  getAdminMeasures: () => authAxios.get("/admin/measures"),
  setMeasureLicence: (gate, enabled) => authAxios.put(`/admin/measures/licences/${gate}`, { enabled }),
  getAdminToolkit: () => authAxios.get("/admin/toolkit"),
  getToolkitTranslations: (code) => authAxios.get(`/admin/toolkit/${code}/translations`),
  saveToolkitTranslation: (code, lang, data) => authAxios.put(`/admin/toolkit/${code}/translations/${lang}`, data),
  getMeasureTranslations: (code) => authAxios.get(`/admin/measures/${code}/translations`),
  saveMeasureTranslation: (code, lang, data) => authAxios.put(`/admin/measures/${code}/translations/${lang}`, data),
  updatePatientAlerts: (data) => authAxios.put("/organizations/patient-alerts", data),
  signNote: (cid, data) => authAxios.post(`/consultations/${cid}/sign`, data),
  reviewNote: (cid, data) => authAxios.post(`/consultations/${cid}/review`, data),
  addNoteAddendum: (cid, text) => authAxios.post(`/consultations/${cid}/addenda`, { text }),
  getNoteReviews: () => authAxios.get("/note-reviews"),
  getClinicalContentManage: (kind) => authAxios.get("/clinical-content/manage", { params: { kind } }),
  createClinicalContent: (data) => authAxios.post("/clinical-content", data),
  updateClinicalContent: (id, data) => authAxios.put(`/clinical-content/${id}`, data),
  submitClinicalContent: (id) => authAxios.post(`/clinical-content/${id}/submit`),
  getClinicalReviewQueue: () => authAxios.get("/clinical-content/review-queue"),
  approveClinicalContent: (id) => authAxios.post(`/clinical-content/${id}/approve`),
  rejectClinicalContent: (id, note) => authAxios.post(`/clinical-content/${id}/reject`, { note }),
  addCareTeamMember: (patientId, data) => authAxios.post(`/patients/${patientId}/care-team`, data),
  removeCareTeamMember: (patientId, memberId) => authAxios.put(`/patients/${patientId}/care-team/${memberId}/remove`),
  fetchOrgFeatures: (orgId) => authAxios.get(`/admin/organizations/${orgId}/features`),
  updateOrgFeatures: (orgId, changes) => authAxios.put(`/admin/organizations/${orgId}/features`, changes),
  markOnboardingStep: (stepId, done) => authAxios.put(`/onboarding/steps/${stepId}`, { done }),
  addSampleData: (doctorUserId) => authAxios.post("/onboarding/sample-data", { doctor_user_id: doctorUserId }),
  removeSampleData: () => authAxios.put("/onboarding/sample-data/remove"),
  updateOwnBankDetails: (data) => authAxios.put("/organizations/bank-details", data),
  updateTrialCampaign: (data) => authAxios.put("/admin/trial-campaign", data),
  updateOrgAiBilling: (orgId, data) => authAxios.put(`/admin/organizations/${orgId}/ai-billing`, data),

  // 🔹 Doctor & Specialization API
  fetchSpecializations: (params) => authAxios.get("/specializations/", { params }),
  fetchDoctors: (params) => authAxios.get("/doctors/", { params }),
  getDoctor: (id) => authAxios.get(`/doctors/${id}`),
  searchDoctor: (term, value) => authAxios.get(`/doctors/${term}/${value}`),
  createDoctor: (data) => authAxios.post("/doctors/", data),
  updateDoctor: (id, data) => authAxios.put(`/doctors/${id}`, data),
  cancelDoctor: (id) => authAxios.delete(`/doctors/${id}`), // soft delete (is_active=false), never a real row deletion
  reactivateDoctor: (id) => authAxios.patch(`/doctors/${id}/reactivate`),
  uploadDoctorImage: (formData) => authAxios.post("/upload/image", formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  generateDoctorPrescription: (data) => authAxios.post("/doctor-agent/generate-prescription", data),
  generateDoctorBio: (data) => authAxios.post("/content-agent/generate-doctor-bio", data),
  rewriteAdviceForPatient: (data) => authAxios.post("/content-agent/rewrite-advice-for-patient", data),
  fetchDoctorExceptions: (doctorId) => authAxios.get(`/doctors/${doctorId}/exceptions`),
  fetchAllDoctorExceptions: () => authAxios.get("/doctors/exceptions"),
  createDoctorException: (doctorId, data) => authAxios.post(`/doctors/${doctorId}/exceptions`, data),
  deleteDoctorException: (doctorId, exceptionId) => authAxios.delete(`/doctors/${doctorId}/exceptions/${exceptionId}`),

  // 🔹 Organization Holidays API
  fetchOrgHolidays: () => authAxios.get("/organizations/holidays"),
  createOrgHoliday: (data) => authAxios.post("/organizations/holidays", data),
  updateOrgHoliday: (id, data) => authAxios.put(`/organizations/holidays/${id}`, data),
  deleteOrgHoliday: (id) => authAxios.delete(`/organizations/holidays/${id}`),

  // 🔹 Organization Branches API
  fetchOrgBranches: (includeInactive) => authAxios.get("/organizations/branches", { params: includeInactive ? { include_inactive: 1 } : {} }),
  createOrgBranch: (data) => authAxios.post("/organizations/branches", data),
  updateOrgBranch: (id, data) => authAxios.put(`/organizations/branches/${id}`, data),
  deleteOrgBranch: (id) => authAxios.delete(`/organizations/branches/${id}`),

  // 🔹 Appointment API
  fetchAppointments: (params) => authAxios.get("/appointments/", { params }),
  getAppointmentDayCounts: (doctorId = null) =>
    authAxios.get("/appointments/dashboard/day-counts", { params: doctorId ? { doctor_id: doctorId } : {} }),
  getAppointment: (id) => authAxios.get(`/appointments/${id}`),
  searchAppointment: (term, value) => authAxios.get(`/appointments/${term}/${value}`),
  createAppointment: (data) => authAxios.post("/appointments/", data),
  getAppointmentSeries: (id) => authAxios.get(`/appointments/series/${id}`),
  endAppointmentSeries: (id) => authAxios.post(`/appointments/series/${id}/end`),
  updateAppointment: (id, data) => authAxios.put(`/appointments/${id}`, data),
  cancelAppointment: (id, data = {}) => authAxios.delete(`/appointments/${id}`, { data }),
  setAppointmentAttendance: (id, attendance) => authAxios.put(`/appointments/${id}/attendance`, { attendance }),
  rescheduleAppointment: (id, data) => authAxios.patch(`/appointments/${id}/reschedule`, data),
  collectAppointmentPayment: (id, data) => authAxios.patch(`/appointments/${id}/collect-payment`, data),
  joinAppointmentMeeting: (id) => authAxios.post(`/appointments/${id}/join`),
  // Patient's recording/transcription consent for this appointment's video
  // session ({ consent: null | { decision: 'granted' | 'declined', ... } }).
  uploadLetterheadLogo: (formData) => authAxios.post("/organizations/letterhead-logo", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  getAppointmentTranscript: (id) => authAxios.get(`/appointments/${id}/transcript`),
  retryAppointmentTranscript: (id) => authAxios.post(`/appointments/${id}/transcript/retry`),
  retagTranscriptSpeakers: (id, roles) => authAxios.put(`/appointments/${id}/transcript/speakers`, { roles }),
  getSessionAssist: (id) => authAxios.get(`/appointments/${id}/session-assist`),
  getNoteFormat: (id) => authAxios.get(`/appointments/${id}/note-format`),
  setNoteFormat: (id, code) => authAxios.put(`/appointments/${id}/note-format`, { code }),
  setSessionAssistSection: (id, data) => authAxios.put(`/appointments/${id}/session-assist/sections`, data),
  retrySessionAssist: (id) => authAxios.post(`/appointments/${id}/session-assist/retry`),
  redraftSessionAssist: (id) => authAxios.post(`/appointments/${id}/session-assist/redraft`),
  getCallFeedback: (id) => authAxios.get(`/appointments/${id}/session-feedback`),
  startCallFeedback: (id) => authAxios.post(`/appointments/${id}/session-feedback`),
  getRecordingConsent: (id) => authAxios.get(`/appointments/${id}/recording-consent`),
  updateRecordingConsent: (id, decision) => authAxios.post(`/appointments/${id}/recording-consent`, { decision }),
  callEnded: (id) => authAxios.post(`/appointments/${id}/call-ended`),
  getSessionTimer: (id) => authAxios.get(`/appointments/${id}/session-timer`),
  extendSession: (id) => authAxios.post(`/appointments/${id}/extend`),
  // Clinic page: its readable code (/c/<code>) and whether it's private (training BE organization/patient_portal.py).
  getOrgPortal: (orgId) => authAxios.get(`/admin/organizations/${orgId}/portal`),
  setOrgPortal: (orgId, data) => authAxios.put(`/admin/organizations/${orgId}/portal`, data),
  // Share a booking link with a patient: { doctor_user_id?, phone?, channel: "sms" | "whatsapp" }.
  shareBookingLink: (data) => authAxios.post("/booking-link/share", data),
  // The clinic's own Admin: its page link and the Private switch.
  getOwnClinicPage: () => authAxios.get("/organizations/clinic-page"),
  setOwnClinicPage: (data) => authAxios.put("/organizations/clinic-page", data),
  getOrgSessionLimits: (orgId) => authAxios.get(`/admin/organizations/${orgId}/session-limits`),
  setOrgSessionLimits: (orgId, limits) => authAxios.put(`/admin/organizations/${orgId}/session-limits`, limits),
  requestRecordingConsent: (id) => authAxios.post(`/appointments/${id}/recording-consent/request`),
  getAppointmentMessages: (id) => authAxios.get(`/appointments/${id}/messages`),
  sendAppointmentMessage: (id, data) => authAxios.post(`/appointments/${id}/messages`, data),
  getUnreadMessageSummary: () => authAxios.get(`/appointments/messages/unread-summary`),
  getPatientChat: (patientId) => authAxios.get(`/patients/${patientId}/chat`),
  sendPatientChat: (patientId, data) => authAxios.post(`/patients/${patientId}/chat`, data),

  //Avatar
  fetchAvatars: () => authAxios.get(`/avatars/`),
  fetchAvatarById: (id) => authAxios.get(`/avatars/${id}`),
  createAvatar: (data) => authAxios.post(`/avatars/`, data),
  updateAvatar: (id, data) => authAxios.put(`/avatars/${id}`, data),
  deleteAvatar: (id) => authAxios.delete(`/avatars/${id}`),

  //Student
  fetchStudents: () => authAxios.get(`/students/`),
  fetchStudentById: (id) => authAxios.get(`/students/${id}`),
  createStudent: (data) => authAxios.post(`/students/`, data),
  updateStudent: (id, data) => authAxios.put(`/students/${id}`, data),
  deleteStudent: (id) => authAxios.delete(`/students/${id}`),

  //Role
  fetchRoles: () => authAxios.get(`/roles/`),

  //User
  fetchUsers: (params) => authAxios.get(`/users/`, { params }),
  getUser: (id) => authAxios.get(`/users/${id}`),
  updateUser: (id, data) => authAxios.put(`/users/${id}`, data),
  deleteUser: (id) => authAxios.delete(`/users/${id}`),

  //Credit
  fetchcreditofloggedinuser: () => authAxios.get(`/credits/user/loggedinuser/`),
  fetchcreditoforg: (receipent_id) => authAxios.get(`/credits/org/?receipent_id=${receipent_id}`),
  fetchcredithistory: (query) => authAxios.get(`/credit-histories/my/${query}`),
  transferCredit: (data) => authAxios.post(`/credits/transfer/`, data),
  notifychattimer: () => authAxios.post(`/credits/notify/timer`),

  //Chat
  endaichatsession: (data) => authAxios.post(`/chats/endsession/`, data),
  restartChatSession: (data) => authAxios.post(`/chat/restart-session`, data),

  
  // Organization Management
  getOrganizations: (includeInactive) => authAxios.get('/organizations/', { params: includeInactive ? { include_inactive: 1 } : {} }),
  createOrganization: (data) => authAxios.post('/organizations/', data),
  updateOrganization: (id, data) => authAxios.put(`/organizations/${id}`, data),
  deactivateOrganization: (id) => authAxios.delete(`/organizations/${id}`),
  reactivateOrganization: (id) => authAxios.post(`/organizations/${id}/reactivate`),
  getLocations: () => authAxios.get('/organizations/locations'),
  uploadBankCheque: (formData) => authAxios.post("/upload/bank-cheque", formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  extractChequeDetails: (chequeImageKey) => authAxios.post('/organizations/extract-cheque-details', { cheque_image_key: chequeImageKey }),
  verifyBankAccount: (id) => authAxios.post(`/organizations/${id}/verify-bank-account`),
  getBankVerificationStatus: (id) => authAxios.get(`/organizations/${id}/verify-bank-account/status`),

  // Organization Settings

  getComparisonSettings: (therapyType = null) => {
    const url = therapyType
      ? `/organizations/settings/comparison?therapy_type=${therapyType}`
      : "/organizations/settings/comparison";
    return authAxios.get(url);
  },
  getGeneralSettings: () => authAxios.get("/organizations/settings/general"),
  getAvailablePhases: () => authAxios.get("/organizations/settings/available-phases"),
  getEffectivePhaseSettings: (therapyType) => authAxios.get(`/organizations/settings/phases/${therapyType}`),
  getEffectivePhaseMetadata: (therapyType) => authAxios.get(`/organizations/settings/phases/${therapyType}/metadata`),
  updateGeneralSettings: (data) => authAxios.put("/organizations/settings/general", data),
  updateComparisonSettings: (therapyType, data) => {
    return authAxios.put("/organizations/settings/comparison", {
      therapy_type: therapyType,
      ...data
    });
  },
  // Organization Config Versioning
  getOrgConfig: (configType) => authAxios.get(`/organizations/config/${configType}`),
  updateOrgConfig: (configType, data) => authAxios.post(`/organizations/config/${configType}`, data),
  resetPhaseSettings: () => authAxios.delete("/organizations/config/phase_settings/reset"),

  // Doctor Settings (for therapy type)
  getDoctorSettings: () => authAxios.get("/doctors/settings/"),
  updateDoctorSettings: (data) => authAxios.put("/doctors/settings/", data),

  // Admin Knowledge Base
  getIngestionStatus: () => authAxios.get("/admin/ingestion/status"),
  listIngestedFiles: () => authAxios.get("/admin/ingestion/files"),
  uploadKnowledgeFile: (formData) => authAxios.post("/admin/ingestion/upload", formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  triggerIngestion: () => authAxios.post("/admin/ingestion/trigger"),
  deleteKnowledgeFile: (filename) => authAxios.delete(`/admin/ingestion/files/${filename}`),
  simulateRetrieval: (data) => authAxios.post("/admin/kb/simulate", data),
  testOverlay: (data) => authAxios.post("/overlay/test", data),

  // Admin Session Upload
  uploadSession: (formData) => authAxios.post("/upload-session", formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Gamification
  getAchievements: () => authAxios.get("/gamification/achievements"),
  getMyAchievements: () => authAxios.get("/gamification/my-achievements"),

  // Admin Manual Review
  getManualReviewStats: (doctorId = null, startDate = null, endDate = null, branchId = null) => {
    const params = {};
    if (doctorId) params.doctor_id = doctorId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (branchId) params.branch_id = branchId;
    return authAxios.get("/stats", { params });
  },
  getManualReviewSettings: (therapyType = null) => {
    const url = therapyType
      ? `/settings?therapy_type=${therapyType}`
      : "/settings";
    return authAxios.get(url);
  },
  updateManualReviewSettings: (data) => authAxios.put("/settings", data),
  getFlaggedSessions: (priority = null) => {
    const params = priority ? { params: { priority } } : {};
    return authAxios.get("/flagged-sessions", params);
  },
  markSessionAsReviewed: (sessionId, _unused, reviewComment = '') =>
    authAxios.post(`/mark-reviewed/${sessionId}`, { review_comment: reviewComment }),
  getSessionReviews: (sessionId) => authAxios.get(`/consultations/${sessionId}/reviews`),

  // Test Connections
  testConnections: () => axios.get(`${BACKEND_URL}/test/connections`, {
    headers: {
      'Content-Type': 'application/json'
    }
  }),

  // Expert Feedback (Person-in-the-Loop)
  getSessionFeedback: (sessionId) => authAxios.get(`/feedback/session/${sessionId}`),
  submitFeedback: (data) => authAxios.post("/feedback/submit", data),
  deleteFeedback: (id) => authAxios.post(`/feedback/delete/${id}`),

  // Error Logs (admin org only)
  getErrorLogs: (params = {}) => authAxios.get("/admin/error-logs", { params }),
  getErrorLogTags: () => authAxios.get("/admin/error-logs/tags"),
  getErrorLogOrgs: () => authAxios.get("/admin/error-logs/orgs"),  // returns distinct org_uid values

  // Admin Consultations Overview (admin org only)
  getAdminConsultationsOverview: (params = {}) => authAxios.get("/admin/consultations-overview", { params }),
  getAdminConsultationOrgs: () => authAxios.get("/admin/consultations-overview/orgs"),
  getAdminConsultationRawSignals: (id) => authAxios.get(`/admin/consultations-overview/${id}/raw-signals`),
  retriggerConsultationSummary: (id, data = {}) => authAxios.post(`/admin/consultations-overview/${id}/retrigger`, data),

  // Payout Batches (admin org only - central ops paying out to client orgs)
  getPayoutOrgsOverview: () => authAxios.get("/admin/payouts/orgs"),
  getEligiblePayoutConsultations: (params = {}) => authAxios.get("/admin/payouts/eligible", { params }),
  createPayoutBatch: (data) => authAxios.post("/admin/payouts/", data),
  getPayoutBatches: (params = {}) => authAxios.get("/admin/payouts/", { params }),
  getPayoutBatch: (id) => authAxios.get(`/admin/payouts/${id}`),
  updatePayoutBatch: (id, data) => authAxios.patch(`/admin/payouts/${id}`, data),

  // Signal Knowledge Base
  getSignalKBEntries: (params = {}) => authAxios.get("/signal-knowledge/entries", { params }),
  getSignalKBSignals: () => authAxios.get("/signal-knowledge/signals"),
  createSignalKBEntry: (data) => authAxios.post("/signal-knowledge/entries", data),
  updateSignalKBEntry: (id, data) => authAxios.put(`/signal-knowledge/entries/${id}`, data),
  deleteSignalKBEntry: (id) => authAxios.delete(`/signal-knowledge/entries/${id}`),
  validateSignalKBEvidence: (data) => authAxios.post("/signal-knowledge/validate", data),
  storeSignalKBEmbedding: (id, data) => authAxios.post(`/signal-knowledge/entries/${id}/embedding`, data),
  embedAllSignalKB: () => authAxios.post("/signal-knowledge/embed-all"),

  // Admin Organization Credits (admin org only)
  getAdminCreditEntities: () => authAxios.get("/credits/admin/entities"),
  addCreditsToEntity: (entityType, entityId, amount) => authAxios.post(`/credits/admin/entities/${entityType}/${entityId}/add`, { amount }),
  getAdminEntityCreditHistory: (entityType, entityId) => authAxios.get(`/credit-histories/admin/entity/${entityType}/${entityId}`),

};
export default api;
